import React from 'react'
import Router from 'next/router'
import getConfig from 'next/config'
import querystring from 'query-string'
import { CmsPage, getNestedObject, RenderCmsComponent } from 'bloomreach-experience-react-sdk'
import { IncomingMessage } from 'http'
import get from 'lodash/get'

import { WebPage } from '@grandvision/analytics'
import { useIntl } from '@grandvision/state'
import {
  BloomreachApi,
  BloomreachService,
  DefaultPageProps,
  getClientSession,
  getPageSeo,
  getServerSession,
  QueryParams,
  Session,
} from '@grandvision/bloomreach'
import { AppointmentBookerRoot } from '@grandvision/appointments-legacy'
import { LocaleUtils } from '@grandvision/utils'

import componentDefinitions from '../cms/component-definitions'
import { isMultilingualEnabled, isStoreSearchEnabled } from '../util/feature-toggle'
import Layout from '../components/Layout'
import AppointmentBookerForm from '../components/forms/AppointmentBooker'

import { NextProcess } from './_app'

const { publicRuntimeConfig } = getConfig()
const apiUrl = publicRuntimeConfig.grandvision.apiUrl || ''
const bloomreachService = BloomreachService(isMultilingualEnabled())
const selfUrl = publicRuntimeConfig.selfUrl || ''
const cmsUrls = publicRuntimeConfig.bloomreach.cmsUrls || {}

type Props = DefaultPageProps & {
  query: QueryParams
  currentRoute: undefined
}

const BookAppointmentPage = ({ pageModel, asPath, request, currentRoute, query }: Props) => {
  const { getLabel } = useIntl()

  if (!pageModel) {
    console.error('No page model defined for appointment page!')
    return null
  }

  const seoData: BloomreachApi.SeoData = getPageSeo(pageModel)

  const consents = [
    {
      title: getLabel('appointments.consent.createAccount'),
      value: 'createAccount',
    },
  ]
  const optIns = [
    {
      title: getLabel('appointments.optIn.marketingPost'),
      value: 'marketingPost',
    },
    {
      title: getLabel('appointments.optIn.marketingEmails'),
      value: 'marketingEmails',
    },
  ]

  // TODO: temporary solution to get the page type in GTM dataLayer, this should be replaced by a global solution to enable
  //  dataLayer from core with initial dynamic data
  React.useEffect(() => {
    if (typeof window !== 'undefined' && window['dataLayer']) {
      window['dataLayer'].push({
        page: {
          type: 'Book Appointment',
        },
      })
    }
  }, [])

  return (
    <CmsPage
      cmsUrls={cmsUrls}
      pageModel={pageModel}
      asPath={asPath}
      request={request}
      componentDefinitions={componentDefinitions}
    >
      {() => (
        <div className="appointment appointment--aab-page">
          <Layout
            pageModel={pageModel}
            asPath={asPath}
            isSolaris={false}
            renderFooter={false}
            renderSiteNav={false}
            isSearchEnabledOnSmallScreens={false}
          >
            <RenderCmsComponent path="main/top-container" />

            <AppointmentBookerRoot
              apiUrl={apiUrl}
              asPath={asPath}
              currentRoute={currentRoute || ''}
              queryParams={query}
              router={Router}
              consents={consents}
              optIns={optIns}
              labels={{}}
              featureToggles={{
                storeSearchEnabled: isStoreSearchEnabled(),
              }}
            >
              <AppointmentBookerRoot.Form>
                <AppointmentBookerForm />
              </AppointmentBookerRoot.Form>
            </AppointmentBookerRoot>

            <RenderCmsComponent path="main/bottom-container" />
            <WebPage
              url={selfUrl + asPath}
              pageType="AppointmentFunnel"
              name={seoData.title}
              description={seoData.description}
            />
          </Layout>
        </div>
      )}
    </CmsPage>
  )
}

BookAppointmentPage.getInitialProps = async ({ asPath, req }) => {
  const defaultLocale = get(publicRuntimeConfig, 'defaultLocale', 'en-IO')
  const nextProcess = process as NextProcess
  // TODO Incorporate this in AppContext (to be created).
  const session: Session = nextProcess.browser ? getClientSession({}) : getServerSession(req)
  const request = {
    hostname: session.host,
    path: '/book-appointment?' + querystring.stringify(session.search),
  }
  const preview = bloomreachService.isCmsPreview(session.search)
  const locale = LocaleUtils.getLocale(req || ({} as any), defaultLocale, preview)
  const pageModel = await bloomreachService.fetchCmsPage(request, locale, session.cookie)

  const { params, query } = (req || {}) as IncomingMessage & {
    params: { currentRoute: string }
    query: { [key: string]: string }
  }
  const { currentRoute = '' } = params || {}

  const pageRoutes = getNestedObject(pageModel, ['page', 'models', 'routes'])

  return {
    asPath,
    pageModel,
    pageRoutes,
    preview,
    request,
    locale,
    currentRoute,
    query,
  } as any
}

export default BookAppointmentPage
